import type { Category, QuickNote, Todo } from "../../domain/entities.ts";
import type { Root } from "../../domain/app-types.ts";
import { escapeHtml } from "../../ui/text.ts";
import { parentTaskId, childTasks } from "./task-hierarchy.ts";

export interface ContextTaskSource {
  kind: "comment" | "reference" | "quick";
  id: string;
  text: string;
  label: string;
  parentId?: string;
  href?: string;
}
export interface ContextTaskDestination { rootId: string; categoryId: string | null }
interface Capabilities {
  document: Document;
  roots(): Root[];
  categories(): Category[];
  tasks(): Todo[];
  selectedTask(): string | null;
  activeRoot(): string | null;
  activeCategory(): string | null;
  quickNote(): QuickNote | undefined;
  flushQuickNote(): Promise<boolean>;
  flushReference(id: string): Promise<boolean>;
  canCreate(destination: ContextTaskDestination): boolean;
  canEdit(task: Todo): boolean;
  create(id: string, source: ContextTaskSource, destination: ContextTaskDestination, title: string, description: string): Promise<void>;
  linkQuickNote(noteId: string, taskId: string): Promise<void>;
  offerUndo(id: string, source: ContextTaskSource, destination: ContextTaskDestination): void;
  openTask(id: string): Promise<void>;
  randomId(): string;
  toast(message: string, error?: boolean): void;
}

export function contextTaskContent(source: ContextTaskSource, title: string): { title: string; description: string } {
  const label = source.kind === "comment" ? "Source comment" : "Source reference";
  return { title: title.trim(), description: source.text + (source.href ? `\n\n[${label}](${source.href})` : "") };
}

export function createContextTaskController(c: Capabilities) {
  let selection: ContextTaskSource | null = null;
  let dialog: HTMLDialogElement | null = null;
  const doc = c.document;

  function modal(label: string) {
    dialog?.close();
    const previous = doc.activeElement as HTMLElement | null;
    const element = doc.createElement("dialog"); element.className = "context-task-dialog"; element.setAttribute("aria-label", label);
    element.addEventListener("keydown", event => event.stopPropagation());
    element.addEventListener("close", () => { element.remove(); if (dialog === element) dialog = null; if (previous?.isConnected) previous.focus(); });
    dialog = element; doc.body.append(element); return element;
  }

  function captureSelection() {
    if (dialog?.open) return;
    const active = doc.activeElement as HTMLTextAreaElement | null;
    let next: ContextTaskSource | null = null;
    const text = active?.tagName === "TEXTAREA" ? active.value.slice(active.selectionStart, active.selectionEnd).trim() : "";
    if (text && active?.matches("[data-note-editor]")) {
      next = { kind: "quick", id: c.quickNote()?.id || "", text, label: "Private Quick Note" };
    } else if (text && active?.matches("[data-reference-input]")) {
      const id = active.dataset.referenceInput!;
      next = { kind: "reference", id, text, label: "Collection reference", href: `?collection=${encodeURIComponent(id)}&note=introduction` };
    } else {
      const range = doc.getSelection();
      const node = range?.anchorNode;
      const section = node?.parentElement?.closest<HTMLElement>(".collection-reference__section");
      if (section && range?.toString().trim() && section.contains(range.focusNode)) {
        const id = section.closest<HTMLElement>("[data-collection-reference]")?.dataset.collectionReference;
        if (id) {
          const slug = section.id.slice(`collection-note-${id}-`.length);
          next = { kind: "reference", id, text: range.toString().trim(), label: "Collection reference", href: `?collection=${encodeURIComponent(id)}&note=${encodeURIComponent(slug)}` };
        }
      }
    }
    // Opening a secondary menu can move focus away from the selected textarea.
    if (next) selection = next;
    else if (!active?.closest?.(".quick-note-menu, .note-selection-actions, .collection-reference__actions, .collection-reference__editor-footer")) selection = null;
    doc.querySelectorAll<HTMLButtonElement>("[data-context-selection]").forEach(button => {
      button.disabled = !selection || button.dataset.contextSelection !== selection.kind;
      button.hidden = button.disabled;
      const bar = button.closest<HTMLElement>("[data-note-selection-actions]");
      if (bar) bar.hidden = button.disabled;
    });
  }

  function current(source: ContextTaskSource): boolean {
    if (source.kind === "quick") {
      const note = c.quickNote();
      return !!note && !note.deleted && note.id === source.id && note.body.includes(source.text) && Object.keys(note.linkedTaskIds || {}).length < 1000;
    }
    if (source.kind === "reference") return c.categories().some(category => category.id === source.id && !category.deleted && !category.archivedAt && category.referenceNote?.includes(source.text));
    const task = c.tasks().find(task => task.id === source.parentId && !task.deleted && !task.archivedAt);
    return !!task && c.canEdit(task) && !parentTaskId(task) && childTasks(c.tasks(), task).length < 100 && !!task.comments?.some(comment => comment.id === source.id && !comment.deletedAt && comment.body === source.text);
  }

  async function open(source: ContextTaskSource) {
    if (dialog?.open) return;
    if (source.kind === "quick") {
      if (!await c.flushQuickNote()) return;
      if (!source.id) source = { ...source, id: c.quickNote()?.id || "" };
    }
    if (source.kind === "reference" && !await c.flushReference(source.id)) return;
    if (!current(source)) { c.toast("The source changed or is no longer available", true); return; }
    if (source.text.length > 19_500) { c.toast("Select a shorter passage (under 19,500 characters)", true); return; }
    const parent = source.kind === "comment" ? c.tasks().find(task => task.id === source.parentId)! : null;
    const destinations: ContextTaskDestination[] = parent ? [{ rootId: parent.rootId, categoryId: parent.categoryId }] : c.roots().filter(root => !root.archived_at).flatMap(root => [
      { rootId: root.id, categoryId: null }, ...c.categories().filter(category => category.rootId === root.id && !category.deleted && !category.archivedAt).map(category => ({ rootId: root.id, categoryId: category.id })),
    ]).filter(c.canCreate);
    if (!destinations.length) { c.toast("There is no editable task destination", true); return; }
    const destinationLabel = (destination: ContextTaskDestination) => `${c.roots().find(root => root.id === destination.rootId)?.name || "Space"} / ${c.categories().find(category => category.id === destination.categoryId)?.name || (destination.categoryId ? "Parent collection" : "No collection")}`;
    const selectedIndex = Math.max(0, destinations.findIndex(destination => destination.rootId === (source.kind === "reference" ? c.categories().find(category => category.id === source.id)?.rootId : c.activeRoot()) && destination.categoryId === (source.kind === "reference" ? source.id : c.activeCategory())));
    const element = modal(parent ? "Create child task from comment" : "Create task from selection");
    const title = source.text.split(/\r?\n/).find(line => line.trim())?.trim().slice(0, 200) || "Follow-up";
    element.innerHTML = `<h2>${parent ? "Create child task" : "Create task from selection"}</h2><form><label>Task title<input name="title" maxlength="200" required value="${escapeHtml(title)}"></label><label>Destination<select name="destination" ${parent ? "disabled" : ""}>${destinations.map((destination, index) => `<option value="${index}" ${index === selectedIndex ? "selected" : ""}>${escapeHtml(destinationLabel(destination))}</option>`).join("")}</select></label><p data-sharing-notice></p><details open><summary>Content to copy</summary><pre class="context-task-preview">${escapeHtml(source.text)}</pre>${source.href ? "<small>A link to the source will be included.</small>" : "<small>The source note stays private. No note link or other note content is shared.</small>"}</details><p role="status" data-context-status></p><div class="dialog-actions"><button type="button" class="button button--soft" data-context-cancel>Cancel</button><button type="submit" class="button button--primary">${parent ? "Create child task" : "Create task"}</button></div></form>`;
    const select = element.querySelector<HTMLSelectElement>("select")!;
    const notice = element.querySelector<HTMLElement>("[data-sharing-notice]")!;
    const updateNotice = () => { notice.textContent = parent ? "This child inherits its parent’s location and access. Anyone who can read it can read the copied comment." : "Only the text shown below will be copied. Anyone with access to the destination task may read it. The source note is not changed or archived."; };
    select.addEventListener("change", updateNotice); updateNotice();
    let pending = false;
    let created = false;
    let createdDestination: ContextTaskDestination | null = null;
    element.addEventListener("close", () => { if (created && createdDestination) c.offerUndo(id, source, createdDestination); });
    const id = c.randomId();
    element.addEventListener("cancel", event => { if (pending) event.preventDefault(); });
    const cancel = element.querySelector<HTMLButtonElement>("[data-context-cancel]")!;
    cancel.addEventListener("click", () => element.close());
    element.querySelector("form")!.addEventListener("submit", async event => {
      event.preventDefault(); if (pending) return;
      const destination = destinations[Number(select.value)];
      const input = element.querySelector<HTMLInputElement>('[name="title"]')!;
      const liveParent = parent ? c.tasks().find(task => task.id === parent.id) : null;
      if (!destination || !input.value.trim() || !current(source) || (!parent && !c.canCreate(destination)) || (parent && (!liveParent || liveParent.rootId !== destination.rootId || liveParent.categoryId !== destination.categoryId))) { c.toast("The source or destination is no longer available", true); return; }
      pending = true; cancel.disabled = true;
      const submit = element.querySelector<HTMLButtonElement>('[type="submit"]')!; submit.disabled = true;
      select.disabled = true; input.readOnly = true;
      try {
        if (!created) {
          const content = contextTaskContent(source, input.value);
          await c.create(id, source, destination, content.title, content.description);
          created = true;
          createdDestination = destination;
        }
        if (source.kind === "quick") await c.linkQuickNote(source.id, id);
        element.close();
      } catch {
        element.querySelector<HTMLElement>("[data-context-status]")!.textContent = created ? "Task saved locally. Retry to finish linking it to the private note; no second task will be created." : "Could not create the task. Your source is unchanged; try again.";
      } finally {
        pending = false; submit.disabled = false; cancel.disabled = false;
        if (!created) { select.disabled = !!parent; input.readOnly = false; }
      }
    });
    element.showModal();
  }

  for (const event of ["selectionchange", "select", "keyup", "pointerup", "input", "focusin"]) doc.addEventListener(event, captureSelection);
  doc.addEventListener("click", event => {
    const element = event.target as Element | null;
    const comment = element?.closest<HTMLElement>("[data-comment-create-child]");
    const selected = element?.closest<HTMLElement>("[data-context-selection]");
    const linked = element?.closest<HTMLElement>("[data-note-linked-tasks]");
    if (!comment && !selected && !linked) return;
    event.preventDefault(); event.stopPropagation();
    if (comment) {
      const task = c.tasks().find(task => task.id === c.selectedTask());
      const item = task?.comments?.find(item => item.id === comment.dataset.commentCreateChild);
      if (task && item && !item.deletedAt) void open({ kind: "comment", id: item.id, parentId: task.id, text: item.body, label: "Comment", href: `?task=${encodeURIComponent(task.id)}&comment=${encodeURIComponent(item.id)}` }).catch(() => c.toast("Could not prepare the task. Your source is unchanged.", true));
    } else if (selected && selection && selection.kind === selected.dataset.contextSelection) {
      if (selection.kind === "quick") doc.querySelector<HTMLDetailsElement>(".quick-note-menu")?.removeAttribute("open");
      void open({ ...selection }).catch(() => c.toast("Could not prepare the task. Your source is unchanged.", true));
    }
    else if (linked) {
      const note = c.quickNote();
      const tasks = c.tasks().filter(task => !task.deleted && !task.archivedAt && note?.linkedTaskIds?.[task.id]);
      const element = modal("Tasks created from this note");
      element.innerHTML = `<h2>Created tasks</h2>${tasks.length ? tasks.map((task, index) => `<button type="button" class="text-button" data-created-task="${index}">${escapeHtml(task.title)}</button>`).join("") : "<p>No linked tasks are currently available on this device.</p>"}<button type="button" class="button button--soft" data-context-cancel>Close</button>`;
      element.querySelectorAll<HTMLButtonElement>("[data-created-task]").forEach(button => button.addEventListener("click", () => { element.close(); void c.openTask(tasks[Number(button.dataset.createdTask)].id).catch(() => c.toast("Could not open the task", true)); }));
      element.querySelector("[data-context-cancel]")!.addEventListener("click", () => element.close()); element.showModal();
    }
  }, true);
  return { open, captureSelection };
}
